import { Link, Outlet, useParams, useRouterState } from '@tanstack/react-router';
import { Bell, LogOut, Plus, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@farmos/ui/button';
import { cn } from '@farmos/ui/utils';
import { useAuth } from '@/auth';
import { unread, useLiveThreads, useThreads } from '@/chat/data';
import { RoundButton, TopBar } from '@/chat/TopBar';
import { needsInstall, pushSupported, subscribePush } from '@/push';
import { supabase } from '@/supabase';

const DISMISSED = 'farmai.push.later';

function PushPrompt() {
  const { t } = useTranslation();
  const [state, setState] = useState(() =>
    !pushSupported() ? 'unsupported' : Notification.permission,
  );
  const [later, setLater] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED) === '1';
    } catch {
      return false;
    }
  });
  // keep this device registered for whoever is signed in now
  useEffect(() => {
    if (state === 'granted') void subscribePush(false).catch(() => undefined);
  }, [state]);
  if (later || state === 'granted' || state === 'denied') return null;
  const dismiss = () => {
    setLater(true);
    try {
      localStorage.setItem(DISMISSED, '1');
    } catch {
      /* storage unavailable */
    }
  };
  return (
    <div className="m-3 rounded-[20px] bg-brand-soft p-3 text-sm text-brand-2">
      <p className="flex gap-2">
        <Bell className="mt-0.5 size-4 shrink-0" aria-hidden />
        {state === 'unsupported' || needsInstall() ? t('push.ios') : t('push.ask')}
      </p>
      <div className="mt-2 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={dismiss}>
          {t('push.later')}
        </Button>
        {state === 'default' && !needsInstall() && (
          <Button size="sm" onClick={() => void subscribePush(true).then(setState)}>
            {t('push.enable')}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * The conversation list (inbox first). On a phone it is its own page (/chats) and FarmAI
 * opens on a new chat; on wide screens the list stays beside the open conversation.
 */
export function ThreadsLayout() {
  const { t, i18n } = useTranslation();
  const { me, session } = useAuth();
  const params = useParams({ strict: false }) as { threadId?: string };
  const onList = useRouterState({ select: (s) => s.location.pathname }) === '/chats';
  const threads = useThreads();
  useLiveThreads(session?.user.id);

  const list = [...(threads.data ?? [])].sort(
    (a, b) => Number(b.kind === 'inbox') - Number(a.kind === 'inbox'),
  );
  const day = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'numeric' });

  return (
    <div className="flex h-dvh">
      <aside
        className={cn(
          'flex w-full flex-col border-e border-line/60 bg-panel/40 backdrop-blur md:w-80 md:shrink-0',
          !onList && 'hidden md:flex',
        )}
      >
        <TopBar
          title={t('threads.list')}
          end={
            <RoundButton
              aria-label={t('common.sign_out')}
              onClick={() => void supabase.auth.signOut()}
            >
              <LogOut className="rtl:-scale-x-100" />
            </RoundButton>
          }
        />
        <div className="px-3 pb-3 pt-1">
          <Button className="w-full shadow-pop" disabled={!me?.enabled} asChild>
            <Link to="/">
              <Plus /> {t('threads.new')}
            </Link>
          </Button>
        </div>
        <PushPrompt />
        <nav aria-label={t('threads.list')} className="flex-1 space-y-1 overflow-y-auto px-2 pb-4">
          {list.map((th) => (
            <Link
              key={th.id}
              to="/t/$threadId"
              params={{ threadId: th.id }}
              className={cn(
                'flex items-center gap-3 rounded-[22px] px-3 py-3 transition-colors hover:bg-panel/80',
                params.threadId === th.id && 'bg-panel shadow-card',
              )}
            >
              {th.kind === 'inbox' ? (
                <span
                  className="grid size-11 shrink-0 place-items-center rounded-full bg-[linear-gradient(135deg,var(--hero-from),var(--hero-to))] text-white shadow-card"
                  aria-hidden
                >
                  <Bell className="size-5" />
                </span>
              ) : (
                <span
                  className="grid size-11 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-2"
                  aria-hidden
                >
                  <Sparkles className="size-5" />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium" dir="auto">
                  {th.kind === 'inbox' ? t('threads.inbox') : th.title || t('threads.untitled')}
                </span>
                <span className="text-xs text-muted">
                  {day.format(new Date(th.last_message_at))}
                </span>
              </span>
              {unread(th) && params.threadId !== th.id && (
                <span className="size-2.5 rounded-full bg-brand" aria-label="•" />
              )}
            </Link>
          ))}
          {threads.isSuccess && list.length <= 1 && (
            <p className="px-4 py-6 text-center text-sm text-muted">{t('threads.empty')}</p>
          )}
        </nav>
      </aside>
      <section className={cn('relative min-w-0 flex-1', onList && 'hidden md:block')}>
        {/* a soft light from the top, in the brand's colours */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(55%_100%_at_75%_0%,color-mix(in_oklab,var(--hero-to)_22%,transparent),transparent_70%),radial-gradient(45%_80%_at_15%_0%,color-mix(in_oklab,var(--brand)_14%,transparent),transparent_70%)]"
        />
        <div className="relative h-full">
          <Outlet />
        </div>
      </section>
    </div>
  );
}
